import React, { useState, useEffect } from 'react';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Stack, Chip, ToggleButton, ToggleButtonGroup, Alert, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import {
  NewReleases as NewIcon,
  History as OldIcon,
  Science as ScienceIcon,
} from '@mui/icons-material';

// 기존 컨테이너
import OrderBookAddModalContainer from '../../../containers/projectmng/obtainorder/OrderBookAddModalContainer';
import OrderBookEditModalContainer from '../../../containers/projectmng/obtainorder/OrderBookEditModalContainer';
import OrderBookHistoryListContainer from '../../../containers/projectmng/obtainorder/OrderBookHistoryListContainer';

// V2 컨테이너
import { OrderBookAddModalContainerV2 } from '../../../containers/projectmng/obtainorder/v2';

import * as chanceApi from '../../../lib/api/chance';
import {
  projectEstimateActive,
  projectOrderCount,
  setProject,
} from '../../../modules/projectModule/project';
import { listProjectCosts } from '../../../modules/projectModule/projectCosts';
import AskDialog from '../../common/dialog/AskDialog';
import BillPlanReplanContainer from '../../../containers/projectmng/billplan/BillPlanReplanContainer';
import { ENTRY_ORDER_LIST } from '../../../lib/api/billReplan';

/**
 * 수주품의서 템플릿
 * 
 * V1(기존)과 V2(개선) UI를 토글로 전환하여 테스트할 수 있습니다.
 * 
 * V2 주요 개선사항:
 * - 카드형 레이아웃
 * - 탭 네비게이션
 * - 매출이익 시각화
 * - 결재선 흐름 표시
 * - 반응형 디자인
 */
const ObtainorderTemplate = ({ setActiveStep, setSelectedTab }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const { project, activeEstimate, orderCount } = useSelector(
    ({ projectModule }) => ({
      project: projectModule.project.project,
      activeEstimate: projectModule.project.activeEstimate,
      orderCount: projectModule.project.orderCount,
    }),
  );

  // UI 버전 선택 ('v1' 또는 'v2') - 기본값 v1 (안정성을 위해)
  const [uiVersion, setUiVersion] = useState('v1');
  
  const [type, setType] = useState('add');
  const [open, setOpen] = useState(false);
  const [roUuid, setRoUuid] = useState(null);

  // V2 전용 상태
  const [openV2, setOpenV2] = useState(false);

  const handleClose = () => {
    setRoUuid(null);
    setType('add');
    setOpen(false);
    setOpenV2(false);
  };

  const editFunction = (param) => {
    setType('edit');
    setRoUuid(param.id);
    setOpen(true);
  };

  const handleAddOrder = () => {
    if (uiVersion === 'v2') {
      setOpenV2(true);
    } else {
      setType('add');
      setOpen(true);
    }
  };

  const onCostUnLock = async () => {
    try {
      const res = await chanceApi.unDoCost(project.prjUuid);

      if (res.data.info) {
        dispatch(setProject(res.data.info));
        dispatch(listProjectCosts(project.prjUuid));
        dispatch(projectEstimateActive(project.prjUuid));
        dispatch(projectOrderCount(project.prjUuid));

        navigate('/chancemng');

        enqueueSnackbar('수주품의서를 재작성하십시오.', {
          variant: 'success',
        });
      }
    } catch (err) {
      let message =
        typeof err.response !== 'undefined'
          ? err.response.data.message
          : err.message;
      enqueueSnackbar(message, {
        variant: 'error',
      });
    }
  };

  const [visible, setVisible] = useState(false);
  const onCancle = () => {
    setVisible(false);
  };
  const onConfirm = () => {
    setVisible(false);
    onCostUnLock();
  };

  useEffect(() => {
    if (project) {
      dispatch(projectOrderCount(project.prjUuid));
      dispatch(projectEstimateActive(project.prjUuid));
    }
  }, [project]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flexWrap: 'wrap',
        m: 1,
      }}
    >
      {/* UI 버전 선택 토글 (개발/테스트용) */}
      <Alert 
        severity="info" 
        icon={<ScienceIcon />}
        sx={{ mb: 2 }}
        action={
          <ToggleButtonGroup
            value={uiVersion}
            exclusive
            onChange={(e, newValue) => {
              if (newValue) setUiVersion(newValue);
            }}
            size="small"
          >
            <ToggleButton value="v1">
              <OldIcon fontSize="small" sx={{ mr: 0.5 }} />
              기존
            </ToggleButton>
            <ToggleButton value="v2">
              <NewIcon fontSize="small" sx={{ mr: 0.5 }} />
              개선(V2)
            </ToggleButton>
          </ToggleButtonGroup>
        }
      >
        <strong>UI 테스트:</strong> {uiVersion === 'v2' ? '개선된 V2 UI를 사용 중입니다.' : '기존 UI를 사용 중입니다.'}
        {uiVersion === 'v2' && ' (탭 네비게이션, 매출이익 차트, 결재선 시각화 등)'}
      </Alert>

      {/* 수주품의서 히스토리 */}
      <OrderBookHistoryListContainer editFunction={editFunction} />

      {/* 세금계산서 발행계획 재분배.
          수주품의서에서의 진입은 권한자만 허용된다(서버 판정). */}
      <Box sx={{ py: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
          세금계산서 발행계획 재분배
        </Typography>
        <BillPlanReplanContainer
          prjUuid={project?.prjUuid}
          entryPoint={ENTRY_ORDER_LIST}
        />
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          py: 1,
        }}
      >
        <Button
          variant="contained"
          color="primary"
          onClick={handleAddOrder}
        >
          수주품의서 작성 {uiVersion === 'v2' && '(V2)'}
        </Button>

        <Button
          variant="contained"
          color="warning"
          onClick={() => setVisible(true)}
        >
          수주품의서 재작성
        </Button>
      </Box>

      {/* 기존 신규 작성 모달 (V1) */}
      {uiVersion === 'v1' && (
        <OrderBookAddModalContainer
          type="add"
          open={type === 'add' && open}
          handleClose={handleClose}
        />
      )}
      
      {/* 수정 모달 (기존 - V1/V2 공통) */}
      <OrderBookEditModalContainer
        type="edit"
        open={type === 'edit' && open}
        handleClose={handleClose}
        roUuid={roUuid}
      />

      {/* V2 신규 작성 모달 */}
      {uiVersion === 'v2' && (
        <OrderBookAddModalContainerV2
          open={openV2}
          handleClose={handleClose}
        />
      )}

      <AskDialog
        visible={visible}
        title="프로젝트 수주품의서 재작성"
        description={`수주품의서를 재작성하시겠습니까?\r\n작업된(견적서 / 수주품의서 ) 등의 사항을 다시 작성해야 하며,\r\n 포캐스팅 리스트로 이동합니다.`}
        confirmText="수주품의서 재작성"
        onConfirm={onConfirm}
        onCancle={onCancle}
      />
    </Box>
  );
};

export default ObtainorderTemplate;
